import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowLeft, ArrowRight } from "lucide-react";

/**
 * Editorial carousel — JCDecaux-style. Sharp arrow controls, no dots.
 * Children render the slides; the parent wraps them in `embla__slide`.
 */
export default function EditorialCarousel({ children, slideClassName = "", options = {}, testId, dark = false }) {
  const [emblaRef, embla] = useEmblaCarousel({ loop: true, align: "start", ...options });
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const [selected, setSelected] = useState(0);

  const onSelect = useCallback(() => {
    if (!embla) return;
    setCanPrev(embla.canScrollPrev());
    setCanNext(embla.canScrollNext());
    setSelected(embla.selectedScrollSnap());
  }, [embla]);

  useEffect(() => {
    if (!embla) return;
    onSelect();
    embla.on("select", onSelect);
    embla.on("reInit", onSelect);
  }, [embla, onSelect]);

  const slides = Array.isArray(children) ? children : [children];

  return (
    <div className="relative" data-testid={testId}>
      <div className="embla" ref={emblaRef}>
        <div className="embla__container">
          {slides.map((child, i) => (
            <div className={`embla__slide ${slideClassName}`} key={i}>
              {child}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 flex items-center justify-between">
        <div className={`text-[11px] tracking-[0.25em] uppercase font-medium ${dark ? "text-white/60" : "text-[var(--slate-2)]"}`}>
          <span className={dark ? "text-[var(--gold-light)]" : "text-[var(--gold)]"}>
            {String(selected + 1).padStart(2, "0")}
          </span>
          {" "}/ {String(slides.length).padStart(2, "0")}
        </div>
        <div className="flex items-center gap-2">
          <CtrlBtn dark={dark} disabled={!canPrev} onClick={() => embla?.scrollPrev()} testId={`${testId}-prev`}>
            <ArrowLeft size={18} />
          </CtrlBtn>
          <CtrlBtn dark={dark} disabled={!canNext} onClick={() => embla?.scrollNext()} testId={`${testId}-next`}>
            <ArrowRight size={18} />
          </CtrlBtn>
        </div>
      </div>
    </div>
  );
}

function CtrlBtn({ children, disabled, onClick, testId, dark }) {
  return (
    <button
      type="button"
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
      className={`w-11 h-11 border flex items-center justify-center transition-all ${
        dark
          ? "border-white/30 text-white hover:bg-white hover:text-[var(--obsidian)]"
          : "border-[var(--ink)] text-[var(--ink)] hover:bg-[var(--ink)] hover:text-[var(--bone)]"
      } disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-current`}
    >
      {children}
    </button>
  );
}
